import { Icon, Tooltip, WithPopover } from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import {
  CurrencyDetails,
  PromotionModal,
  PromotionModalOptions,
  TabTypes,
} from '@vend/business-components'
import classNames from 'classnames'
import React, { FunctionComponent, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  useCustomerManager,
  useRegisterManager,
  useRetailerSettingsManager,
  useSaleManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectCurrentSale,
  selectSalePromoCodePromotionId,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { getPromotionById } from '../../../promotions/promotions-resource/promotions-resource'
import { getDisabledTooltipMessage, TooltipContext } from '../add-row-tooltips'
import { isPromotionDisabled } from '../promotion-functions'
import { AddPromoCodePopover } from './AddPromoCodePopover'

export const PromoCode: FunctionComponent = () => {
  const [showPopover, setShowPopover] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [
    modalOptions,
    setModalOptions,
  ] = useState<PromotionModalOptions | null>(null)

  const t = useT()
  const saleManager = useSaleManager()
  const customerManager = useCustomerManager()
  const registerManager = useRegisterManager()
  const retailerSettingsManager = useRetailerSettingsManager()
  const promoCodePromotionId = useAppSelector(selectSalePromoCodePromotionId)
  const currentSale = useAppSelector(selectCurrentSale)

  const disabledPromotions = isPromotionDisabled(
    saleManager.hasActiveSale(),
    saleManager.getHasActivePromotions(),
    currentSale
  )

  const handlePromotionClick = () => {
    if (disabledPromotions) {
      return
    }

    setShowPopover(true)
  }

  const openPromoModal = async () => {
    if (!promoCodePromotionId) {
      return
    }

    const promotion = await getPromotionById(promoCodePromotionId)
    if (!promotion) {
      return
    }

    const retailer = retailerSettingsManager.get()
    const currencyDetails: CurrencyDetails = {
      culture: retailer.culture,
      code: retailer.currency.code,
      symbol: retailer.currency.symbol,
    }
    const lineItemIds = saleManager.getLineItemProductIds()
    const isTaxExclusive = !registerManager.isTaxInclusive()
    const customerGroups = await customerManager.getCustomerGroups()

    setModalOptions({
      promotion,
      lineItemIds,
      isTaxExclusive,
      totalCustomerGroups: customerGroups ? customerGroups.length : 0,
      defaultTab: TabTypes.PromoCode,
      currencyDetails,
    })

    // Close the popover before opening the modal, so that they don't overlap
    // Also, interacting will the modal will close the popover anyway
    setShowPopover(false)
    setShowModal(true)
  }

  const disabledPromoCodeMessage = getDisabledTooltipMessage(
    t,
    currentSale,
    TooltipContext.PROMOTION
  )

  return (
    <>
      {showModal &&
        modalOptions &&
        createPortal(
          <PromotionModal
            close={() => setShowModal(false)}
            options={modalOptions}
          />,
          document.body
        )}

      <WithPopover direction="left">
        <Tooltip
          className="wr-tooltip"
          direction="left"
          canBeShown={
            disabledPromotions && disabledPromoCodeMessage !== undefined
          }
          header={disabledPromoCodeMessage?.title ?? ''}
          message={disabledPromoCodeMessage?.message ?? ''}
          icon="info-circle"
        >
          <span
            className={classNames(
              'vd-text-label pro-sale-promo-code-link sale-total-header--link',
              {
                'vd-link': !disabledPromotions,
                'sale-link-disabled': disabledPromotions,
              }
            )}
            onClick={handlePromotionClick}
          >
            {disabledPromotions && <Icon icon="fa-lock" className="vd-mr2" />}{' '}
            <T _str="Promo code" _context="Promo code link on sale footer" />
          </span>
        </Tooltip>

        {showPopover && (
          <AddPromoCodePopover
            closePopover={() => setShowPopover(false)}
            openPromoModal={openPromoModal}
          />
        )}
      </WithPopover>
    </>
  )
}
